<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useSettingsStore } from '@/stores/settingsStore';

const settingsStore = useSettingsStore();

const isEnabled = computed(() => {
  const now = new Date();
  return now.getMonth() === 11 && settingsStore.snowEnabled; // Respect store and month
});

const canvasRef = ref<HTMLCanvasElement | null>(null);
let ctx: CanvasRenderingContext2D | null = null;
let animationId: number | null = null;
let particles: Particle[] = [];
const mouse = { x: -1000, y: -1000 };

class Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
  color: string;

  constructor(width: number, height: number, color: string) {
    this.x = Math.random() * width;
    this.y = Math.random() * height;
    this.size = Math.random() * 10 + 15; // Reduced size: 15 to 25px
    this.speedX = Math.random() * 0.4 - 0.2; // Slower horizontal speed
    this.speedY = Math.random() * 0.5 + 0.3; // Slower vertical speed: 0.3 to 0.8
    this.opacity = Math.random() * 0.5 + 0.3;
    this.color = color;
  }

  update(width: number, height: number, color: string) {
    this.color = color;
    
    // Mouse interaction: Divert snowflakes
    const dx = this.x - mouse.x;
    const dy = this.y - mouse.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const forceThreshold = 150;

    if (distance < forceThreshold) {
      const force = (forceThreshold - distance) / forceThreshold;
      // Reduced interaction speed for smoother diversion
      this.x += (dx / distance) * force * 3;
      this.y += (dy / distance) * force * 3;
    }

    this.x += this.speedX;
    this.y += this.speedY;

    if (this.y > height + 20) {
      this.y = -20;
      this.x = Math.random() * width;
    }
    if (this.x > width + 20) this.x = -20;
    if (this.x < -20) this.x = width + 20;
  }

  draw() {
    if (!ctx) return;
    ctx.font = `${this.size}px serif`;
    ctx.fillStyle = this.color;
    ctx.globalAlpha = this.opacity;
    
    // Adding shadow for better visibility on light backgrounds
    ctx.shadowBlur = 3;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    
    ctx.fillText('❄', this.x, this.y);
    
    // Reset shadow for other drawings if any (good practice)
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }
}

const getSnowColor = () => {
  const isDark = document.documentElement.classList.contains('dark');
  // Blueish shade for light mode (#4682b4 is SteelBlue)
  return isDark ? '#ffffff' : '#4682b4'; 
};

const initParticles = () => {
  if (!canvasRef.value) return;
  particles = [];
  const color = getSnowColor();
  // Calculate flake count based on width for responsive density (approx 1 flake per 25px)
  const count = Math.max(15, Math.floor(canvasRef.value.width / 25));
  for (let i = 0; i < count; i++) {
    particles.push(new Particle(canvasRef.value.width, canvasRef.value.height, color));
  }
};

const animate = () => {
  if (!ctx || !canvasRef.value) return;
  ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
  
  const color = getSnowColor();
  particles.forEach(p => {
    p.update(canvasRef.value!.width, canvasRef.value!.height, color);
    p.draw();
  });
  
  animationId = requestAnimationFrame(animate);
};

const handleResize = () => {
  if (canvasRef.value) {
    canvasRef.value.width = window.innerWidth;
    canvasRef.value.height = window.innerHeight;
    initParticles();
  }
};

const handleMouseMove = (e: MouseEvent) => {
  mouse.x = e.clientX;
  mouse.y = e.clientY;
};

const startAnimation = () => {
  if (canvasRef.value && !animationId) {
    ctx = canvasRef.value.getContext('2d');
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    animate();
  }
};

const stopAnimation = () => {
  if (animationId) {
    cancelAnimationFrame(animationId);
    animationId = null;
  }
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('mousemove', handleMouseMove);
};

onMounted(() => {
  if (isEnabled.value) {
    startAnimation();
  }
});

onUnmounted(() => {
  stopAnimation();
});

// Re-init if enabled state changes
watch(isEnabled, async (val) => {
  if (val) {
    await nextTick();
    startAnimation();
  } else {
    stopAnimation();
  }
});
</script>

<template>
  <canvas
    v-if="isEnabled"
    ref="canvasRef"
    class="snow-canvas"
    aria-hidden="true"
  ></canvas>
</template>

<style scoped>
.snow-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9999;
}
</style>
